import { useMutation } from "react-query";
import { FileData } from "../@types/files";
import { each, find, isMatch, last, map, take, zip } from "lodash";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

type UploadFileArgs = {
  fileData: {
    clientId: string;
    file: File & FileData;
    storageDirectory: string;
  };
  setProgress: (data: File & FileData) => void;
};
async function uploadFile({ fileData, setProgress }: UploadFileArgs) {
  const data = new FormData();
  data.append("file", fileData.file);
  data.append("name", fileData.file.name);
  data.append("type", fileData.file.type);
  data.append("external_id", fileData.clientId);

  const response = await axios.post(
    `/api/storage/${fileData.storageDirectory}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: ({ loaded, total }) => {
        if (total) {
          const progress = (loaded * 100) / total;
          setProgress({
            ...fileData.file,
            name: fileData.file.name,
            type: fileData.file.type,
            size: fileData.file.size,
            clientId: fileData.clientId,
            progress,
          });
        }
      },
    },
  );

  return response.data;
}

export default function useFileUpload({
  selectedFiles: filesValue,
  setSelectedFiles,
}: {
  selectedFiles: FileData[];
  setSelectedFiles: (files: (File & FileData)[]) => void;
}) {
  let selectedFiles = filesValue || [];
  // Values sent to onChange being reflected in value take some time, so we have to store the real value locally
  // in case setProgress is fired quickly, i.e. when selecting multiple files
  const onChangeLocal = (newValue) => {
    selectedFiles = newValue;
    setSelectedFiles(newValue);
  };

  function setProgress(data: File & FileData) {
    const newValue = map(selectedFiles, (f) =>
      isMatch(f, { clientId: data.clientId }) ? { ...f, ...data } : f,
    );

    if (!find(selectedFiles, { clientId: data.clientId })) {
      newValue.push(data);
    }
    onChangeLocal(newValue);
  }

  const { mutate: onUploadFile } = useMutation(
    (fileData: {
      clientId: string;
      file: File & FileData;
      storageDirectory: string;
    }) => uploadFile({ fileData, setProgress }),
    {
      onSuccess: (data, variables) => {
        setProgress({
          ...data,
          id: data.id,
          name: variables.file.name,
          size: variables.file.size,
          type: variables.file.type,
          clientId: variables.clientId,
          state: "volatile",
          progress: 100,
        });
      },
      onError: (error: { response: { status: string } }, data) => {
        setProgress({
          ...data.file,
          clientId: data.clientId,
          progress: 0,
          state: "failed",
          status: error.response.status,
        });
      },
    },
  );

  function handleUploadFiles(
    rawFiles: (File & FileData)[],
    storageDirectory,
    maxFiles,
  ) {
    let rawFilesToUpload = rawFiles;
    if (maxFiles) {
      const existingFiles = selectedFiles || [];
      rawFilesToUpload = take(rawFiles, maxFiles - existingFiles.length);
    }

    const filesData = map(rawFilesToUpload, (file) => ({
      name: file.name,
      type: file.type,
      extension: last(file.name.split(".")),
      clientId: uuidv4(),
      progress: 0,
      state: "uploading",
    }));

    const newValue = [...(selectedFiles || []), ...filesData];
    onChangeLocal(newValue);

    each(zip(rawFilesToUpload, filesData), (zippedPair) => {
      const [file, fileData] = zippedPair;
      if (file && fileData) {
        onUploadFile({
          clientId: fileData.clientId,
          file,
          storageDirectory,
        });
      }
    });
  }

  return handleUploadFiles;
}
